<script setup lang="ts">
import { bookingGlobalPopupStore } from "@/components/booking/bookingGlobalPopupStore";
import { useUserModeChange } from "@/store/useUserModeChange";
import { useActivityStore } from "@/utils/ActivityStore";
import { formatPrice, GSTFromTotal, Round2Num } from "@/utils/Formatting";
import type { DocketLinesRecord, DocketPaymentsRecord, DocketsRecord, LocationRecordModel } from "@/utils/PocketBaseAdapter";
import { pb } from "@/utils/PocketBaseAdapter";
import { moment } from "@/utils/useTimeZone";
import { storeToRefs } from "pinia";
import { RecordModel } from "pocketbase";
import { useRouter } from "vue-router";

//Props
interface Props {
  docketId?: string;
  showDialog?: boolean;
  allowPaylink?: boolean;
}

const props = defineProps<Props>();

// Stores
const activityStore = useActivityStore();
const router = useRouter()
const { openPopup } = bookingGlobalPopupStore()
const { bookingLoading } = storeToRefs(bookingGlobalPopupStore())
const { getActivitiesMap } = activityStore;
const { activitiesMap } = storeToRefs(activityStore);
const modeStore = useUserModeChange();
const { isUserMode } = storeToRefs(modeStore);

const emit = defineEmits<{
  (e: 'update:showDialog', value: boolean): void;
}>();

const closeDialog = () => {
  emit('update:showDialog', false);
};

// Reactive Data
const docketTotal = ref<number | undefined>(0);
const docketSubtotal = ref<number | undefined>(0);
const docketTotalDiscount = ref<number | undefined>(0);
const docketTotalTodDiscount = ref<number | undefined>(0);
const docketTotalIncrease = ref<number | undefined>(0);
const creditCardSurcharge = ref<number | undefined>(0);
const creditCardTip = ref<number | undefined>(0);
const paymentsTotal = ref<number | undefined>(0);
const docketLines = ref<DocketLinesRecord[] | RecordModel[]>([]);
const docketPayments = ref<DocketPaymentsRecord[] | RecordModel[]>([]);
const paymentRequests = ref<RecordModel[]>([]);
const docketLocationDetails = ref<LocationRecordModel | null>(null);
const isReceiptDialogOpen = ref<boolean>(false);
const openedDocketId = ref<string>("");
const areasMap = ref<Map<string, string>>(new Map());
const isDocketLineHistoryDialogOpen = ref<boolean>(false);
const selectedDocketLineId = ref<string>("");
const isGiftVoucherDetailDialogOpen = ref<boolean>(false);
const selectedGiftVoucher = ref<any>(null);
const loyaltyEarned = ref<number>(0);

const docketDetails = ref<DocketsRecord | null>(null);

// Computed
const isAdmin = computed(() => {
  return pb.authStore?.model?.roles.length > 0 && !isUserMode.value;
});

const getUserDetails = computed(() => {
  const {
    firstName,
    lastName,
    address,
    postcode,
    suburb,
    city,
    state,
    country,
  } =
  docketDetails.value?.expand?.user ||
  docketLines.value[0]?.expand?.userAccountCredit ||
  docketPayments.value[0]?.expand?.user ||
  {};

  return {
    firstName: firstName ?? "",
    lastName: lastName ?? "",
    address: address ?? "",
    postcode: postcode ?? "",
    suburb: suburb ?? "",
    city: city ?? "",
    state: state ?? "",
    country: country ?? "",
  };
});

const getDiscountSource = computed(() => {
  let content = "";
  const membershipName = docketDetails?.value?.expand?.user?.expand?.membership?.name;
  const discountCode = docketDetails?.value?.expand?.discountCode?.simpleCode;
  const discountSources = docketDetails?.value?.discountSources;

  // Check if there are applicable discounts
  if (discountSources) {
    const sources = [];

    if (discountSources.includes("POS")) {
      sources.push("POS");
    }
    if (discountSources.includes("DiscountCode") && discountCode) {
      sources.push(`Discount code ${discountCode}`);
    }
    if (discountSources.includes("Membership") && membershipName) {
      sources.push(`${membershipName}`);
    }

    // Join all sources with commas
    content = sources.join(", ");
  }

  if(content !== "") {
    const plural = content.includes(',') ? "s" : "";
    content = `${content} discount${plural} applied.`
  }

  return content ? content : "";
});

const getTodSource = computed(() => {
  const todSourceList = docketLines.value.reduce((acc: string[], line) => {
    if(line?.todSource && !acc.includes(line.expand?.todSource?.name)){
      acc.push(line.expand?.todSource?.name)
    }
    return acc;
  }, [])

  if(todSourceList.length > 0 && hasTodAdjustment.value){
    return `Time of day pricing applied ${todSourceList.join(", ")}.`
  }

  return "";
});

const fetchLoyaltyEarned = async() => {
  const transactions = await pb.collection("transactions").getFullList({
    filter: `docket="${props?.docketId}" && transactionType="Credit" && valueType="Loyalty"`,
  });
  loyaltyEarned.value = transactions.reduce((acc, transaction) => {
    return acc + (transaction.amount ?? 0);
  }, 0);
}

const hasCardFailures = computed(() => {
  return docketPayments.value.some((payment) => payment.cardFailureCode);
});

const hasDiscounts = computed(() => {
  return docketLines.value.some((line) => line?.lineDiscount || line?.docketDiscount);
});

const hasTodAdjustment = computed(() => {
  return docketLines.value.some((line) =>
    line?.todAdjustment != 0 && (line?.discountApplied === 'TOD' || line?.todAdjustment > 0)
  );
});

// Older dockets never had subTotal/discount saved on the docket record - fall back
// to calculating subTotal from the docketLines the old way when both are unset
const isLegacyDocketTotals = computed(() => {
  return !docketDetails.value?.subTotal && !docketDetails.value?.discount;
});

const displaySubtotal = computed(() => {
  return isLegacyDocketTotals.value ? docketSubtotal.value : docketDetails.value?.subTotal;
});

// docket.discount is the actual (possibly cap-clamped) combined promo+TOD discount.
// The cap clamps the combined figure only, with no record of how much of it was TOD -
// so the promo-only figure shown here is derived by removing TOD's raw (uncapped) share
// from the real capped total, rather than re-summing the uncapped promo lines directly
// (which could show more discount than was actually taken off once the cap applies).
const displayTotalDiscount = computed(() => {
  if (isLegacyDocketTotals.value) {
    return docketTotalDiscount.value;
  }
  const cappedCombined = docketDetails.value?.discount ?? 0;
  const todDiscount = docketTotalTodDiscount.value ?? 0;

  return Math.max(0, Round2Num(cappedCombined - todDiscount, 2));
});

// Methods
// Fetch the docket details with the docket id
const docketStatus = ref('')
const getDocketDetails = async () => {
  try {
    const record = await pb.collection("dockets").getOne(props?.docketId, {
      expand: "location,user.membership,discountCode",
    });

    let allowView = props.allowPaylink === true
    if(!allowView && pb.authStore.model?.roles && pb.authStore.model?.roles.length  > 0){
      allowView = true
    } else if(!allowView && pb.authStore?.model?.id && record.user && (record.user == pb.authStore.model.id || record.createdBy == pb.authStore.model.id)){
      allowView = true
    }

    if(!allowView) {
      const getAllDocketLines = await pb.collection("docketLines").getFullList({
        filter: 'docket="' + props?.docketId + '"',
        expand: "booking",
      });

      const userId = pb?.authStore?.model?.id
      getAllDocketLines.forEach((docketLine) => {
        if(userId && docketLine?.expand?.booking?.owner == userId){
          allowView = true
        }
        if(userId && docketLine?.expand?.booking?.users && docketLine?.expand?.booking?.users?.includes(userId)){
          allowView = true
        }
      })
    }

    if(!allowView){
      await router.push("/")
      return
    }
    
    docketDetails.value = record as DocketsRecord;
    docketStatus.value = record.status
    docketLocationDetails.value = docketDetails.value?.expand?.location;

    // Fetch related details
    await getDocketPayments();
    await getDocketLines();
    await getPaymentRequests();

    // Calculate docketTotal with two decimal places as a number
    docketTotal.value = parseFloat(
      (
        docketDetails.value.total +
        (creditCardSurcharge.value ?? 0) +
        (creditCardTip.value ?? 0)
      ).toFixed(2),
    );
  } catch (error) {
    console.error("Error fetching docket details:", error);
  }
};

// Fetch the docket payment details for the current docket
const getDocketPayments = async () => {
  try {
    const paymentRecords = await pb.collection("docketPayments").getFullList({
      filter: `docket="${props?.docketId}"`,
      expand: "user,giftVoucher",
    });

    // filter docket payments with 'pending' or 'waiting' status
    docketPayments.value = paymentRecords.filter((record) => {
      return !["Waiting", "Pending"].includes(record.status);
    });

    if (paymentRecords.length > 0) {
      // Calculate creditCardSurcharge
      creditCardSurcharge.value = paymentRecords.reduce(
        (accumulator: number, currentValue: any) => {
          if (currentValue.status === "Approved") {
            return accumulator + (currentValue.surchargeAmount ?? 0);
          }
          return accumulator;
        },
        0,
      );

      // Calculate creditCardtip
      creditCardTip.value = paymentRecords.reduce(
        (accumulator: number, currentValue: any) => {
          if (currentValue.status === "Approved") {
            return accumulator + (currentValue.tipAmount ?? 0);
          }
          return accumulator;
        },
        0,
      );

      // Calculate paymentsTotal
      paymentsTotal.value = paymentRecords.reduce(
        (accumulator, currentValue) => {
          if (currentValue.status === "Approved") {
            return (
              accumulator +
              (currentValue.amount ?? 0) +
              (currentValue.surchargeAmount ?? 0) +
              (currentValue.tipAmount ?? 0)
            );
          }
          return accumulator;
        },
        0,
      );
    }
  } catch (error) {
    console.error("Error fetching docket payments:", error);
  }
};

// Fetch payment requests for the current docket
const getPaymentRequests = async () => {
  try {
    paymentRequests.value = await pb.collection("onlinePaymentRequests").getFullList({
      filter: `docket="${props?.docketId}"`,
      sort: "-updated",
    });
  } catch (error) {
    console.error("Error fetching payment requests:", error);
  }
};

// Fetch the docket line details for the current docket
const getDocketLines = async () => {
  try {
    let lineRecords = await pb.collection("docketLines").getFullList({
      filter: `docket="${props?.docketId}"`,
      expand: "userAccountCredit,product,docket,booking,giftVoucher,coupon,docketLineOptions_via_docketLine,todSource",
    });

    if (lineRecords.length > 0) {
      if(!isAdmin.value){
        lineRecords = lineRecords.filter((record) => {
          return record.qty != 0;
        });
      }
      docketLines.value = lineRecords;

      let totalPrice = 0;
      let totalDiscount = 0;
      let totalTodDiscount = 0;
      let totalTodAdjustment = 0;
      for (let i = 0; i < lineRecords?.length; i++) {
        const { lineDiscountTotal, itemPrice, qty, todAdjustment, discountApplied } = lineRecords[i];
        totalPrice = totalPrice + qty * itemPrice;
        if(discountApplied != "TOD"){
          totalDiscount += lineDiscountTotal;
        } else {
          totalTodDiscount += lineDiscountTotal;
        }

        if(todAdjustment && (discountApplied === "TOD" || todAdjustment > 0)){
          totalTodAdjustment += itemPrice * qty * (todAdjustment / 100)
        }
      }
      // Only floor the TOD figure when a cap is actually configured - otherwise
      // (no cap, or a legacy docket with no discountLimit saved) leave it uncapped
      const discountLimit = Number(docketDetails.value?.discountLimit) || 0;
      if (discountLimit > 0) {
        totalTodAdjustment = Math.max(totalTodAdjustment, -discountLimit);
      }

      docketSubtotal.value = Round2Num(totalPrice, 2);
      docketTotalDiscount.value = Round2Num(totalDiscount, 2);
      docketTotalTodDiscount.value = Round2Num(totalTodDiscount, 2);
      docketTotalIncrease.value = Round2Num(totalTodAdjustment, 2);
    }
  } catch (error) {}
};

// Method to get the docketline type
const getDocketLineType = (item: DocketLinesRecord) => {
  if (item.transactionType == "Booking Payment") {
    return (
      `${activitiesMap.value.get(item.expand?.booking?.activity)} Booking`
    );
  }
  if (item.transactionType == "Coupon") {
    const couponLabel = item?.couponQty > 1 ? "Coupons" : "Coupon";
    return `${item?.couponQty} ${couponLabel} - ${item.coupon}` || "";
  }
  if (item.transactionType == "UserCredit") {
    return "User Credit";
  }
  if (item.transactionType == "Membership Fee") {
    return "Membership Fee";
  }

  if (item.transactionType == "Event" && item?.booking) {
    return (
      item.expand?.booking?.area ? `Event Bay ${areasMap.value.get(item.expand?.booking?.area)}` : "Event Bay"
    );
  }

  return item.expand?.product?.name || "";
};

const handleReceiptClick = () => {
  try {
    isReceiptDialogOpen.value = true;
    openedDocketId.value = props?.docketId ?? "";
  } catch (e) {}
};

const openDocketLineHistory = (docketLineId: string) => {
  selectedDocketLineId.value = docketLineId;
  isDocketLineHistoryDialogOpen.value = true;
};

const openGiftVoucherDetail = (giftVoucher: any) => {
  selectedGiftVoucher.value = giftVoucher;
  isGiftVoucherDetailDialogOpen.value = true;
};

const goToEventTicket = (eventTicketId: string) => {
  router.push({ name: "events", query: { tab: "1", search: eventTicketId } });
};

const getAreasMap = async () => {
  areasMap.value = await pb.collection("areas").getFullList({ filter: `location = "${docketDetails.value?.location}"` }).then((areas) => {
    const map = new Map<string, string>();
    areas.forEach((area) => {
      map.set(area.id, area.areaNumber);
    });
    return map;
  });
};

// Lifecycle Hooks
const loadDocketData = async () => {
  if (props.docketId) {
    await getDocketDetails();
    await getActivitiesMap();
    await getAreasMap();
    await fetchLoyaltyEarned();
  }
};

const resetDocketData = () => {
  docketTotal.value = 0;
  docketSubtotal.value = 0;
  docketTotalDiscount.value = 0;
  docketTotalTodDiscount.value = 0;
  docketTotalIncrease.value = 0;
  creditCardSurcharge.value = 0;
  creditCardTip.value = 0;
  paymentsTotal.value = 0;
  docketLines.value = [];
  docketPayments.value = [];
  paymentRequests.value = [];
  docketLocationDetails.value = null;
  docketDetails.value = null;
  docketStatus.value = '';
  isReceiptDialogOpen.value = false;
  openedDocketId.value = '';
  isDocketLineHistoryDialogOpen.value = false;
  selectedDocketLineId.value = '';
  isGiftVoucherDetailDialogOpen.value = false;
  selectedGiftVoucher.value = null;
  loyaltyEarned.value = 0;
};

// Watch for dialog opening to load data
watch(() => props.showDialog, async (newVal) => {
  if (newVal && props.docketId) {
    await loadDocketData();
  } else if (!newVal) {
    resetDocketData();
  }
}, { immediate: true });

</script>

<template>
  <v-dialog
    :model-value="props.showDialog"
    @update:model-value="emit('update:showDialog', $event)"
    max-width="1200px"
    persistent
    no-click-animation
  >
    <v-card>
      <v-card-title class="d-flex border-b mb-2 pb-1 pr-1 pt-1 align-center">
        Docket Viewer
        <v-spacer/>
        <v-btn icon="mdi-times" variant="text" @click="closeDialog"/>
      </v-card-title>
      <div id="docket-print">
        <VContainer class="pb-0">
          <!-- user Details-->
          <VRow>
            <VCol class="text-left">
              <div
                v-if="
                  docketDetails?.expand?.user ||
                  docketLines[0]?.userAccountCredit ||
                  docketPayments[0]?.user
                "
              >
                <VCardSubtitle class="pa-0 text-body">
                  {{
                    `${getUserDetails.firstName} ${getUserDetails.lastName}`
                  }}</VCardSubtitle
                >
                <VCardText class="pa-0">
                  {{ getUserDetails.address }},
                  {{ getUserDetails.suburb }}
                </VCardText>
                <VCardText class="pa-0">
                  {{ getUserDetails.city }},
                  {{ getUserDetails.postcode }}
                </VCardText>
              </div>
              <div v-else>
                <VCardTitle>Cash Sale</VCardTitle>
              </div>
            </VCol>
            <VCol class="text-right">
              <VCardTitle>
                <VBtn v-if="docketStatus != 'Paid' && docketStatus != 'Closed'" link :href="'/pos?did=' + props?.docketId"
                      variant="plain" class="pa-0">Docket #{{ docketDetails?.docketNumber }}</VBtn>
                <span v-else>Docket #{{ docketDetails?.docketNumber }}</span>
              </VCardTitle>
              <VCardText class="pb-0 pt-1">
                Date Created: {{ moment(docketDetails?.created).format("DD MMM YYYY h:mma") }}
              </VCardText>
            </VCol>
          </VRow>
        </VContainer>
        <!-- docketLine items table  -->
        <VContainer>
          <VTable hover class="text-no-wrap border rounded">
            <thead>
              <tr>
                <th>Qty</th>
                <th>Description</th>
                <th class="text-right">Item Price</th>
                <th class="text-right">Line Total</th>
                <th
                  v-if="docketLines.length && hasTodAdjustment"
                  class="text-right"
                >
                  TOD adjustment
                </th>
                <th
                  v-if="docketLines.length && hasDiscounts"
                  class="text-right"
                >
                  Discount
                </th>
                <th
                  class="text-right"
                  v-if="docketLines.length && (hasDiscounts || hasTodAdjustment)"
                >
                  Discount Total
                </th>
              </tr>
            </thead>
            <tbody>
            <tr v-for="item in docketLines" :key="item.id">
              <!-- Quantity -->
              <td>
                <span>{{ item.qty }}</span>
                <v-btn v-if="isAdmin" icon="mdi-history" variant="text" class="rounded-0 ml-2" color="" @click="openDocketLineHistory(item.id ?? '')" />
              </td>

              <!-- For Gift Vouchers -->
              <td v-if="item.transactionType === 'Gift Voucher'">
                <div style="white-space: pre-line;">
                  Gift Voucher -
                  <template v-for="(giftVoucherId, index) in item.giftVoucher" :key="giftVoucherId">
                    <VBtn
                      class="pa-0"
                      variant="plain"
                      @click="openGiftVoucherDetail(item.expand?.giftVoucher?.[index])"
                    >{{ giftVoucherId }}</VBtn>
                    <span v-if="index < item.giftVoucher.length - 1">,</span><br />
                  </template>
                </div>
              </td>

              <!-- For Event Tickets -->
              <td v-else-if="item.transactionType === 'Event'">
                <div style="white-space: pre-line;">
                  {{ item.expand?.product?.name || getDocketLineType(item) || "Event Bay" }}
                  <span v-if="item.eventTicket.length"> - </span>
                  <template v-for="(eventTicket, index) in item.eventTicket" :key="eventTicket">
                    <a href="javascript:void(0)" @click="goToEventTicket(eventTicket)">{{ eventTicket }}</a>
                    <span v-if="index < item.eventTicket.length - 1">,</span>
                    <br />
                  </template>
                </div>
              </td>

              <!-- For other transaction types -->
              <td v-else-if="item.transactionType === 'UserCredit'">Account Credit</td>
              <td v-else-if="item.transactionType === 'User Loyalty'">Loyalty Credit</td>
              <td v-else>
                <VBtn
                  v-if="item?.booking && item.booking != ''"
                  class="pa-0"
                  variant="plain"
                  :loading="bookingLoading"
                  @click="openPopup(item.booking)"
                >
                  {{ getDocketLineType(item) }}
                </VBtn>
                <span v-else>{{ getDocketLineType(item) }}</span>
                <div v-if="item.expand?.docketLineOptions_via_docketLine">
                  <span 
                    v-for="option in item.expand.docketLineOptions_via_docketLine" 
                    :key="option.id"
                    class="d-block text-body-2"
                  >
                    {{ option?.option }}
                  </span>
                </div>
                <span v-if="item?.lineText && item?.lineText !=''" class="ms-1">- {{item.lineText}}</span>
              </td>

              <!-- Price -->
              <td class="text-right"><span>{{ formatPrice(item.itemPrice) }}</span></td>

              <!-- Line total without discount -->
              <td class="text-right"><span>{{ formatPrice(item.itemPrice * item.qty) }}</span></td>

              <!-- TOD -->
              <td
                v-if="docketLines.length && hasTodAdjustment"
                class="text-right">
                <div v-if="item.todAdjustment != 0 && (item.discountApplied === 'TOD' || item.todAdjustment > 0)">
                  {{ item?.todAdjustment }}%
                  <v-tooltip>
                    <template #activator="{ props }">
                      <v-icon
                        v-bind="props"
                        icon="mdi-information-outline"
                        size="xs"
                      />
                    </template>
                    <span>{{ item?.expand?.todSource?.name }}</span>
                  </v-tooltip>
                </div>
              </td>

              <!-- Discount -->
              <td
                v-if="docketLines.length && hasDiscounts"
                class="text-right">
                <div v-if="item.discountApplied === 'Line' && item.lineDiscount">
                  {{ item?.lineDiscount }}%
                  <v-tooltip>
                    <template #activator="{ props }">
                      <v-icon
                        v-bind="props"
                        icon="mdi-information-outline"
                        size="xs"
                      />
                    </template>
                    <span>Manually applied discount to item</span>
                  </v-tooltip>
                </div>
                <div v-if="(item.discountApplied === 'Docket Manual' || item.discountApplied === 'Membership' || item.discountApplied === 'Code') && item.docketDiscount">
                  {{ item?.docketDiscount }}%
                  <v-tooltip>
                    <template #activator="{ props }">
                      <v-icon
                        v-bind="props"
                        icon="mdi-information-outline"
                        size="xs"
                      />
                    </template>
                    <span>{{ item.discountApplied }}</span>
                  </v-tooltip>
                </div>
              </td>

              <!-- lineTotal with discount -->
              <td class="text-right" v-if="docketLines.length && (hasDiscounts || docketLines.some((line) => line?.todAdjustment))">
                <span>{{ formatPrice(item.lineTotal) }}</span>
              </td>
            </tr>
            </tbody>
          </VTable>
        </VContainer>
        <!-- docketLines details  -->
        <VContainer class="pb-0 pt-0">
          <VRow>
            <VCol sm="6" cols="12" class="d-flex">
              <VRow>
                <VCol cols="12">
                  <div v-if="getDiscountSource">{{ getDiscountSource }}</div>
                  <div v-if="getTodSource">{{ getTodSource }}</div>
                  <div v-if="loyaltyEarned > 0">{{ formatPrice(loyaltyEarned) }} Loyalty earned on this transaction</div>
                  <div v-if="docketDetails?.discount && docketDetails?.discount === docketDetails?.discountLimit">
                    Discounts have been capped at ${{ docketDetails?.discountLimit }}.
                  </div>
                </VCol>
                <VCol class="d-flex">
                  <p class="align-self-end">
                    Total Includes {{ docketLocationDetails?.taxName }} of:
                    <span>{{
                        formatPrice(
                          GSTFromTotal(
                            Number(docketDetails?.total),
                            docketLocationDetails?.taxRate ?? 0,
                          ),
                        )
                      }}</span>
                  </p>
                </VCol>
              </VRow>
            </VCol>

            <VCol sm="6" cols="12">
              <VRow>
                <VCol class="text-right pe-6">
                  <!-- Subtotal -->
                  <div
                    v-if="
                      (displayTotalDiscount ?? 0) ||
                      (docketTotalIncrease ?? 0) ||
                      (creditCardSurcharge ?? 0) ||
                      (creditCardTip ?? 0)
                    "
                  >
                    <p>
                      Subtotal:
                      <span class="ps-4 font-weight-medium">
                        {{formatPrice(displaySubtotal)}}
                      </span>
                    </p>
                  </div>
                  <!-- Total Increase -->
                  <div v-if="docketTotalIncrease">
                    <p>
                      TOD adjustment:
                      <span class="ps-4 font-weight-medium">
                        {{formatPrice(docketTotalIncrease)}}
                      </span>
                    </p>
                  </div>
                  <!-- Total Discount (promo portion; excludes TOD, which is broken out separately above) -->
                  <div v-if="displayTotalDiscount">
                    <p>
                      Total Discount:
                      <span class="ps-4 font-weight-medium">
                        -{{formatPrice(displayTotalDiscount)}}
                      </span>
                    </p>
                  </div>
                  <!--Credit Card Surcharge -->
                  <div v-if="creditCardSurcharge">
                    <p>
                      Credit Card Surcharge:
                      <span class="ps-4 font-weight-medium">{{
                          formatPrice(creditCardSurcharge)
                        }}</span>
                    </p>
                  </div>
                  <!--Credit Card Tip -->
                  <div v-if="creditCardTip">
                    <p>
                      Tips:
                      <span class="ps-4 font-weight-medium">{{
                          formatPrice(creditCardTip)
                        }}</span>
                    </p>
                  </div>
                  <!-- Total -->
                  <p class="text-h6">
                    Total:
                    <span class="ps-4 font-weight-medium">{{
                        formatPrice(docketTotal)
                      }}</span>
                  </p>
                </VCol>
              </VRow>
            </VCol>
          </VRow>
        </VContainer>
        <!-- docket payments table  -->
        <VContainer>
          <VTable hover class="text-no-wrap border rounded">
            <thead>
            <tr>
              <th>Source</th>
              <th>Payment Type</th>
              <th>Date/Time</th>
              <th>Status</th>
              <th v-if="hasCardFailures">Card Failure</th>
              <th class="text-right">Amount</th>
              <th
                v-if="
                    docketPayments.length &&
                    docketPayments.some((payment) => payment?.surchargeAmount)
                  "
                class="text-right"
              >
                Surcharge
              </th>
              <th
                v-if="
                    docketPayments.length &&
                    docketPayments.some((payment) => payment?.tipAmount)
                  "
                class="text-right"
              >
                Tip
              </th>
              <th class="text-right">Total</th>
            </tr>
            </thead>
            <tbody v-if="docketPayments.length">
            <tr v-for="item in docketPayments" :key="item.id">
              <td>
                {{ item.source }}
              </td>
              <td>
                {{ item.paymentType }}
                <VBtn
                  v-if="item.giftVoucher"
                  class="pa-0"
                  variant="plain"
                  @click="openGiftVoucherDetail((item as any).expand?.giftVoucher)"
                >- {{ item.giftVoucher }}</VBtn>
                <span v-if="item.paymentType === 'Coupon' && item.couponQty > 1" class="ms-1">x {{ item.couponQty }}</span>
              </td>
              <td>
                <p>{{ moment(item.created).format("DD-MM-YYYY h:mma") }}</p>
              </td>
              <td>
                {{ item.status }}
              </td>
              <td v-if="hasCardFailures">{{ item.cardFailureCode || "" }}</td>
              <td class="text-right">
                {{ formatPrice(item.amount) }}
              </td>
              <td
                v-if="
                    docketPayments.length &&
                    docketPayments.some((payment) => payment?.surchargeAmount)
                  "
                class="text-right"
              >
                <div v-if="item.surchargeAmount">
                  {{ formatPrice(item.surchargeAmount) }}
                </div>
                <div v-else></div>
              </td>

              <td
                v-if="
                    docketPayments.length &&
                    docketPayments.some((payment) => payment?.tipAmount)
                  "
                class="text-right"
              >
                <div v-if="item.tipAmount">
                  {{ formatPrice(item.tipAmount) }}
                </div>
                <div v-else></div>
              </td>
              <td class="text-right">
                  <span v-if="item.status === 'Approved'">
                    {{
                      formatPrice(
                        item.surchargeAmount + item.tipAmount + item.amount,
                      )
                    }}
                  </span>
                <span v-else> $0.00 </span>
              </td>
            </tr>
            </tbody>
          </VTable>
        </VContainer>
        <!-- docket amount due  -->
        <VContainer>
          <VRow class="align-center">
            <VCol col='12' md="8">
              <template v-if="paymentRequests.length">
                <p class="text-subtitle-2 mb-2">Payment Requests</p>
                <VTable density="compact" class="border rounded">
                  <thead>
                    <tr>
                      <th>Date/Time</th>
                      <th>Status</th>
                      <th class="text-right">Amount</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="req in paymentRequests" :key="req.id">
                      <td>{{ moment(req.updated).format("DD MMM YYYY h:mma") }}</td>
                      <td>{{ req.status }}</td>
                      <td class="text-right">{{ formatPrice(req.amount) }}</td>
                    </tr>
                  </tbody>
                </VTable>
              </template>
            </VCol>

            <VCol class="text-right pe-6">
              <p class="text-h6">
                Total Paid:
                <span class="ps-4">{{ formatPrice(paymentsTotal) }}</span>
              </p>
              <p class="text-h6">
                Amount Due:
                <span class="ps-4">{{ formatPrice(docketDetails?.due || 0) }}</span>
              </p>
            </VCol>
          </VRow>
        </VContainer>
        <v-divider/>
        <v-card-actions class="pt-2">
          <v-spacer/>
          <VBtn color="success" variant="flat" prepend-icon="mdi-receipt-text-send" @click="handleReceiptClick">Email/Print</VBtn>
          <VBtn color="error" variant="flat" @click="closeDialog"> Close </VBtn>
        </v-card-actions>
        <ReceiptDialog
          :is-visible="isReceiptDialogOpen"
          :docket-id="openedDocketId"
          @close-dialog="
            () => {
              (isReceiptDialogOpen = false), (openedDocketId = '');
            }
          "
        />
        <DocketLineHistoryDialog
          :is-visible="isDocketLineHistoryDialogOpen"
          :docket-line-id="selectedDocketLineId"
          @close-dialog="
            () => {
              isDocketLineHistoryDialogOpen = false;
              selectedDocketLineId = '';
            }
          "
        />
        <GiftVoucherDetail
          :is-dialog-open="isGiftVoucherDetailDialogOpen"
          :is-editing-gift-voucher="false"
          :selected-gift-voucher="selectedGiftVoucher"
          @close-dialog="isGiftVoucherDetailDialogOpen = false"
        />
      </div>
    </v-card>
  </v-dialog>
</template>

<style lang="scss" scoped>
p {
  margin-block-end: 8px;
}

.logoImage {
  block-size: 294px;
  inline-size: 400px;
  object-fit: contain;

  @media (max-width: 600px) {
    block-size: auto;
    inline-size: 100%;
  }
}
</style>
